import { useCallback, useMemo, useState } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";

import {
  createSurveyQuestionId,
  getDefaultQuestionDataForType,
  type SurveyQuestionType,
} from "../data";
import type { SurveyFormValues, SurveyQuestion } from "../types/survey.types";

const cloneQuestion = (question: SurveyQuestion): SurveyQuestion => ({
  ...question,
  data: {
    ...question.data,
    options: question.data.options?.map((option) => ({ ...option })),
    rows: question.data.rows?.map((row) => ({ ...row })),
    columns: question.data.columns?.map((column) => ({ ...column })),
    rating_labels: question.data.rating_labels && { ...question.data.rating_labels },
  },
});

const normalizeQuestions = (questions: SurveyQuestion[]) =>
  questions.map((question, index) => ({ ...question, position: index + 1 }));

const EMPTY_QUESTIONS: SurveyQuestion[] = [];

export const useSurveyQuestionEditor = (form: UseFormReturn<SurveyFormValues>) => {
  const [lastAddedQuestionId, setLastAddedQuestionId] = useState<string | null>(null);
  const watchedQuestions = useWatch({ control: form.control, name: "questions" });
  const questions = watchedQuestions ?? EMPTY_QUESTIONS;

  const commitQuestions = useCallback(
    (nextQuestions: SurveyQuestion[]) => {
      form.setValue("questions", normalizeQuestions(nextQuestions), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [form],
  );

  const addQuestion = useCallback(
    (type: SurveyQuestionType) => {
      const clientId = createSurveyQuestionId();
      commitQuestions([
        ...questions,
        {
          client_id: clientId,
          is_required: false,
          type,
          position: questions.length + 1,
          data: { question: "", ...getDefaultQuestionDataForType(type) },
        },
      ]);
      setLastAddedQuestionId(clientId);
      return clientId;
    },
    [commitQuestions, questions],
  );

  const updateQuestion = useCallback(
    (index: number, updates: Partial<SurveyQuestion>) => {
      commitQuestions(
        questions.map((question, currentIndex) =>
          currentIndex === index ? { ...question, ...updates } : question,
        ),
      );
    },
    [commitQuestions, questions],
  );

  const deleteQuestion = useCallback(
    (index: number) =>
      commitQuestions(questions.filter((_, currentIndex) => currentIndex !== index)),
    [commitQuestions, questions],
  );

  const duplicateQuestion = useCallback(
    (index: number) => {
      const source = questions[index];
      if (!source) return "";

      const clientId = createSurveyQuestionId();
      const duplicate = cloneQuestion(source);
      commitQuestions([
        ...questions,
        {
          ...duplicate,
          client_id: clientId,
          position: questions.length + 1,
          data: { ...duplicate.data, question: `${source.data.question} (Copy)` },
        },
      ]);
      setLastAddedQuestionId(clientId);
      return clientId;
    },
    [commitQuestions, questions],
  );

  const moveQuestion = useCallback(
    (index: number, direction: -1 | 1) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= questions.length) return;

      const nextQuestions = [...questions];
      const [movedQuestion] = nextQuestions.splice(index, 1);
      nextQuestions.splice(targetIndex, 0, movedQuestion);
      commitQuestions(nextQuestions);
    },
    [commitQuestions, questions],
  );

  const actions = useMemo(
    () => ({
      add: addQuestion,
      duplicate: duplicateQuestion,
      delete: deleteQuestion,
      update: updateQuestion,
      move: moveQuestion,
      reorder: commitQuestions,
    }),
    [addQuestion, commitQuestions, deleteQuestion, duplicateQuestion, moveQuestion, updateQuestion],
  );

  return { questions, lastAddedQuestionId, actions };
};
